import { useState, useCallback, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { PDFViewer } from '@/components/common/pdf-viewer';
import { XlsxViewer } from '@/components/xlsx-viewer/XlsxViewer';
import { attachmentViewerFileKind } from './attachmentViewerFileKind';
import { T } from 'gt-react';
import {
  MagnifyingGlassPlusIcon,
  MagnifyingGlassMinusIcon,
  ArrowCounterClockwiseIcon,
  DownloadSimpleIcon,
} from '@phosphor-icons/react';

interface AttachmentViewerModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  attachmentUrl: string;
  title?: string;
}

function getFileName(url: string): string {
  try {
    const pathname = new URL(url).pathname;
    const segments = pathname.split('/');
    return decodeURIComponent(segments[segments.length - 1] || 'attachment');
  } catch {
    return 'attachment';
  }
}

const ZOOM_STEP = 0.1;
const MIN_ZOOM = 0.5;
const MAX_ZOOM = 5;

export function AttachmentViewerModal({
  open,
  onOpenChange,
  attachmentUrl,
  title = 'View Attachment',
}: AttachmentViewerModalProps) {
  const fileType = attachmentViewerFileKind(attachmentUrl);
  const fileName = getFileName(attachmentUrl);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const dragRef = useRef<{
    startX: number;
    startY: number;
    panX: number;
    panY: number;
  } | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const zoomIn = useCallback(
    () => setZoom((z) => Math.min(z + ZOOM_STEP, MAX_ZOOM)),
    [],
  );
  const zoomOut = useCallback(
    () => setZoom((z) => Math.max(z - ZOOM_STEP, MIN_ZOOM)),
    [],
  );
  const resetView = useCallback(() => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
  }, []);

  const handleWheel = useCallback((e: React.WheelEvent) => {
    if (e.ctrlKey || e.metaKey) {
      e.preventDefault();
      setZoom((z) =>
        e.deltaY < 0
          ? Math.min(z + ZOOM_STEP, MAX_ZOOM)
          : Math.max(z - ZOOM_STEP, MIN_ZOOM),
      );
    }
  }, []);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (zoom <= 1) return;
      e.preventDefault();
      dragRef.current = {
        startX: e.clientX,
        startY: e.clientY,
        panX: pan.x,
        panY: pan.y,
      };

      const handleMouseMove = (ev: MouseEvent) => {
        if (!dragRef.current) return;
        setPan({
          x: dragRef.current.panX + (ev.clientX - dragRef.current.startX),
          y: dragRef.current.panY + (ev.clientY - dragRef.current.startY),
        });
      };

      const handleMouseUp = () => {
        dragRef.current = null;
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };

      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    },
    [zoom, pan],
  );

  function renderContent() {
    switch (fileType) {
      case 'pdf':
        return (
          <div className="h-[78vh] rounded-md border overflow-hidden">
            <PDFViewer url={attachmentUrl} />
          </div>
        );

      // The local parser, not `ExcelDocViewer`: this modal is handed a bare URL
      // with no `documentId`, which is what the Office Online path needs. It
      // brings its own fullscreen and column-expand controls.
      case 'spreadsheet':
        return (
          <div className="h-[78vh]">
            <XlsxViewer url={attachmentUrl} fileName={fileName} height="100%" />
          </div>
        );

      case 'image':
        return (
          <div
            ref={containerRef}
            className="overflow-hidden bg-muted/30 rounded-md"
            style={{
              maxHeight: '70vh',
              cursor:
                zoom > 1 ? (dragRef.current ? 'grabbing' : 'grab') : 'default',
            }}
            onWheel={handleWheel}
            onMouseDown={handleMouseDown}
          >
            <div className="flex items-center justify-center p-4 min-h-[200px]">
              <img
                src={attachmentUrl}
                alt={fileName}
                className="rounded-md select-none"
                style={{
                  transform: `scale(${zoom}) translate(${pan.x / zoom}px, ${pan.y / zoom}px)`,
                  transformOrigin: 'center center',
                  maxWidth: '100%',
                  maxHeight: zoom <= 1 ? '65vh' : undefined,
                  objectFit: 'contain',
                  transition: dragRef.current ? 'none' : 'transform 0.15s ease',
                }}
                draggable={false}
              />
            </div>
          </div>
        );

      // No local parser for Word, and no `documentId` to hand Office Online —
      // the header's Download button is the honest answer.
      case 'document':
      case 'unknown':
      default:
        return (
          <div className="flex flex-col items-center justify-center py-12 space-y-4">
            <div className="text-center">
              <p className="text-lg font-medium">{fileName}</p>
              <p className="text-sm text-muted-foreground mt-1">
                <T>This file type cannot be previewed directly.</T>
              </p>
            </div>
          </div>
        );
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) resetView();
        onOpenChange(isOpen);
      }}
      // Don't auto-close on overlay click — the overlay unmounts between
      // pointerdown and pointerup, so the trailing click would land on whatever
      // is underneath (e.g. the row the paperclip sits on, opening the Discount
      // Detail modal). Close via the X or Escape instead.
      disableBackdropClickClose
    >
      {/* Write the cap twice: `DialogContent`'s base classes end in
          `sm:max-w-lg`, and twMerge does not treat an unprefixed cap as a
          conflict with a prefixed one, so the variant would win above 640px. */}
      <DialogContent className="w-[94vw] max-w-[1500px] max-h-[94vh] sm:max-w-[1500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between pr-8">
            <span className="truncate mr-2">{title}</span>
            <div className="flex items-center gap-1 shrink-0">
              {fileType === 'image' && (
                <>
                  <Button variant="outline" size="icon" onClick={zoomOut}>
                    <MagnifyingGlassMinusIcon className="size-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="min-w-[52px] text-xs tabular-nums"
                    onClick={resetView}
                  >
                    {Math.round(zoom * 100)}%
                  </Button>
                  <Button variant="outline" size="icon" onClick={zoomIn}>
                    <MagnifyingGlassPlusIcon className="size-4" />
                  </Button>
                  {zoom > 1 && (
                    <Button variant="ghost" size="icon" onClick={resetView}>
                      <ArrowCounterClockwiseIcon className="size-4" />
                    </Button>
                  )}
                </>
              )}
              <Button variant="outline" size="sm" asChild>
                <a
                  href={attachmentUrl}
                  download={fileName}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <DownloadSimpleIcon className="size-4 mr-1.5" />
                  <T context="download attachment button">Download</T>
                </a>
              </Button>
            </div>
          </DialogTitle>
        </DialogHeader>
        <div className="overflow-auto">{renderContent()}</div>
      </DialogContent>
    </Dialog>
  );
}
